import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ChevronRightIcon, UserIcon, ShieldCheckIcon, SparklesIcon, CheckIcon } from '@heroicons/react/24/outline';
import { ODISHA_DISTRICTS, getDistrictBlocks } from '../../utils/helpers';
import { checkIfRegistrationIdExists } from '../../services/firebaseService';

// Validation schema for Step 1
const step1Schema = yup.object({
  firstName: yup.string().trim().required('First name is required').min(2, 'Minimum 2 characters'),
  lastName: yup.string().trim().required('Last name is required').min(2, 'Minimum 2 characters'),
  dateOfBirth: yup.string().required('Date of birth is required').test('not-future', 'Date of birth cannot be in the future', (val) => {
    if (!val) return false;
    const dob = new Date(val);
    const today = new Date();
    return dob < today;
  }),
  gender: yup.mixed<'male' | 'female' | 'other'>().required('Gender is required').oneOf(['male', 'female', 'other']),
  bloodGroup: yup.string().required('Blood group is required'),
  fatherName: yup.string().trim().required('Father\'s name is required').min(2, 'Minimum 2 characters'),
  motherName: yup.string().trim().required('Mother\'s name is required').min(2, 'Minimum 2 characters'),
  nationality: yup.string().trim().required('Nationality is required'),
  religion: yup.string().trim().required('Religion is required'),
  caste: yup.mixed<'General' | 'OBC' | 'SC' | 'ST'>().required('Caste is required').oneOf(['General', 'OBC', 'SC', 'ST']),
  aadhaarNumber: yup.string().required('Aadhaar is required').matches(/^\d{12}$/, 'Enter 12-digit Aadhaar number'),
  heightCm: yup.number()
    .transform((val, orig) => (orig === '' || orig === null || orig === undefined ? undefined : val))
    .typeError('Height must be a valid number')
    .positive('Height must be positive')
    .min(50, 'Minimum height is 50 cm')
    .max(300, 'Maximum height is 300 cm')
    .required('Height is required'),
  weightKg: yup.number()
    .transform((val, orig) => (orig === '' || orig === null || orig === undefined ? undefined : val))
    .typeError('Weight must be a valid number')
    .positive('Weight must be positive')
    .min(20, 'Minimum weight is 20 kg')
    .max(300, 'Maximum weight is 300 kg')
    .required('Weight is required'),
  sportsCategory: yup.string().optional(),
  eventsStateLevel: yup.boolean().default(false),
  eventsNationalLevel: yup.boolean().default(false),
  nccGrade: yup.mixed<'A'|'B'|'C'|'None'>().required().oneOf(['A','B','C','None']),
  address: yup.object({
    street: yup.string().trim().required('Street address is required'),
    village: yup.string().trim().required('Village is required'),
    postOffice: yup.string().trim().required('Post Office is required'),
    policeStation: yup.string().trim().required('Police Station is required'),
    block: yup.string().required('Block is required'),
    district: yup.string().required('District is required'),
    state: yup.string().default('Odisha'),
    pincode: yup.string().required('Pincode is required').matches(/^\d{6}$/, 'Enter 6-digit pincode')
  }),
  mobileNumber: yup.string().required('Mobile number is required').matches(/^\d{10}$/, 'Enter 10-digit mobile number'),
  emailId: yup.string().trim().required('Email is required').email('Invalid email format'),
  specialId: yup.string()
    .test('valid-special-id', 'Sequence must be between 0001 and 1000', (val) => {
      if (!val || val.trim() === '') return true;
      const num = parseInt(val.trim(), 10);
      return !isNaN(num) && num >= 1 && num <= 1000;
    })
    .optional()
});

type Step1Data = yup.InferType<typeof step1Schema>;

interface PersonalDetailsStepProps {
  data: Partial<Step1Data>;
  onNext: (data: Step1Data, shouldDirectSubmit?: boolean) => void;
  onBack?: () => void;
  isSubmitting?: boolean;
}

const PersonalDetailsStep: React.FC<PersonalDetailsStepProps> = ({ data, onNext, onBack, isSubmitting }) => {
  const initialDistrict = data.address?.district || '';
  const [selectedDistrict, setSelectedDistrict] = useState(initialDistrict);
  const [availableBlocks, setAvailableBlocks] = useState<string[]>(() => 
    initialDistrict ? getDistrictBlocks(initialDistrict) : []
  );
  
  const [specialIdStatus, setSpecialIdStatus] = useState<{ status: 'idle' | 'checking' | 'available' | 'taken'; message?: string }>({ status: 'idle' });

  const { 
    register, 
    handleSubmit, 
    formState: { errors },
    watch,
    setValue
  } = useForm<Step1Data>({
    resolver: yupResolver(step1Schema),
    defaultValues: {
      ...data,
      address: {
        state: 'Odisha',
        ...data.address
      },
      eventsStateLevel: data.eventsStateLevel || false,
      eventsNationalLevel: data.eventsNationalLevel || false,
      nccGrade: data.nccGrade || 'None'
    }
  });

  const watchDistrict = watch('address.district');
  const watchSpecialId = watch('specialId');
  const prevDistrictRef = React.useRef(initialDistrict);
  const checkReqCounter = React.useRef(0);

  // Debounced effect for checking availability whenever watchSpecialId changes
  React.useEffect(() => {
    const rawVal = watchSpecialId?.trim() || '';
    if (!rawVal) {
      setSpecialIdStatus({ status: 'idle' });
      return;
    }

    // While user is entering leading zeros like "0", "00", "000", keep idle without flashing errors
    if (rawVal === '0' || rawVal === '00' || rawVal === '000') {
      setSpecialIdStatus({ status: 'idle' });
      return;
    }

    const num = parseInt(rawVal, 10);
    if (isNaN(num) || num < 1 || num > 1000) {
      setSpecialIdStatus({ status: 'taken', message: 'Form Number must be between 0001 and 1000' });
      return;
    }

    const seq = String(num).padStart(4, '0');
    setSpecialIdStatus({ status: 'checking', message: `Checking availability for Form No. ${seq}...` });

    const currentReqId = ++checkReqCounter.current;
    const timer = setTimeout(async () => {
      try {
        const exists = await checkIfRegistrationIdExists(seq);
        if (currentReqId === checkReqCounter.current) {
          if (exists) {
            setSpecialIdStatus({ status: 'taken', message: `Form No. ${seq} is already taken.` });
          } else {
            setSpecialIdStatus({ status: 'available', message: `Form No. ${seq} is available!` });
          }
        }
      } catch {
        if (currentReqId === checkReqCounter.current) {
          setSpecialIdStatus({ status: 'idle' });
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [watchSpecialId]);

  React.useEffect(() => {
    if (watchDistrict) {
      const blocks = getDistrictBlocks(watchDistrict);
      setAvailableBlocks(blocks);
      setSelectedDistrict(watchDistrict);
      
      // Reset block only if district was actively changed by the user
      if (prevDistrictRef.current && prevDistrictRef.current !== watchDistrict) {
        setValue('address.block', '');
      }
      prevDistrictRef.current = watchDistrict;
    } else {
      setAvailableBlocks([]);
      setSelectedDistrict('');
      prevDistrictRef.current = '';
    }
  }, [watchDistrict, setValue]);

  const onSubmit = (formData: Step1Data) => {
    if (specialIdStatus.status === 'checking') {
      alert('Please wait a moment while we verify the availability of the Form Number.');
      return;
    }
    if (specialIdStatus.status === 'taken') {
      alert(`The Form Number entered (${specialIdStatus.message || 'Already taken'}) is not available. Please choose a different number or leave it blank.`);
      return;
    }
    onNext(formData);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="flex items-center mb-8">
          <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center mr-4">
            <UserIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Personal & Contact Details</h2>
            <p className="text-gray-600">Please provide your basic information</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Special ID / Form Number Allotment */}
          <div className="border rounded-xl p-5 mb-6 transition-all duration-300 bg-primary-50/50 border-primary-100">
            <h3 className="text-sm font-semibold text-primary-900 mb-2 flex items-center justify-between">
              <span className="flex items-center">
                <ShieldCheckIcon className="w-5 h-5 mr-2 text-primary-600" />
                Form Number / Special ID Selection
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                <SparklesIcon className="w-3.5 h-3.5 mr-1 text-emerald-600" />
                Fast Track Submission Available
              </span>
            </h3>
            <p className="text-xs text-primary-700 mb-3 max-w-2xl leading-relaxed">
              Enter your physical Form Number (0001 - 1000) or special sequence if applicable. If left blank, the system automatically assigns the next sequential registration number.
            </p>
            
            <div className="mb-4 p-3 bg-white border border-emerald-200 rounded-lg flex items-start space-x-2 text-xs text-emerald-800">
              <SparklesIcon className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-bold">Important:</span> Step 1 (Personal Details) is <span className="font-semibold underline">mandatory</span>. Step 2 (Education Details) and Step 3 (Document Upload) are <span className="font-semibold underline">optional</span> and can be skipped for all applications.
              </div>
            </div>

            <div className="max-w-xs">
              <label className="block text-xs font-semibold text-primary-900 mb-2">
                Form Number / Special ID (0001 - 1000) (Optional)
              </label>
              <input
                type="text"
                maxLength={4}
                placeholder="e.g. 0007 (Leave blank for default auto-generation)"
                {...register('specialId')}
                className="w-full px-3 py-2.5 border border-primary-200 rounded-lg text-sm bg-white text-primary-950 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all font-mono"
              />
              {specialIdStatus.status === 'checking' && (
                <p className="text-xs text-primary-600 mt-1.5 flex items-center animate-pulse">
                  <span className="inline-block w-2 h-2 rounded-full bg-primary-600 mr-1.5 animate-ping"></span>
                  <span>{specialIdStatus.message || 'Checking availability...'}</span>
                </p>
              )}
              {specialIdStatus.status === 'available' && (
                <p className="text-xs text-emerald-700 font-semibold mt-1.5 flex items-center">
                  <CheckIcon className="w-4 h-4 mr-1 text-emerald-600 flex-shrink-0" />
                  <span>{specialIdStatus.message}</span>
                </p>
              )}
              {specialIdStatus.status === 'taken' && (
                <p className="text-xs text-red-600 font-semibold mt-1.5 flex items-center">
                  <span className="mr-1 text-red-600 font-bold">✕</span>
                  <span>{specialIdStatus.message}</span>
                </p>
              )}
            </div>
          </div>

          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">
                First Name <span className="text-xs font-bold text-primary-600 ml-1">(As per 10th Certificate)</span> <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('firstName')}
                className={`form-input ${errors.firstName ? 'border-red-500' : ''}`}
                placeholder="Enter your first name"
              />
              {errors.firstName && (
                <p className="text-red-500 text-sm mt-1">{errors.firstName.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">
                Last Name <span className="text-xs font-bold text-primary-600 ml-1">(As per 10th Certificate)</span> <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('lastName')}
                className={`form-input ${errors.lastName ? 'border-red-500' : ''}`}
                placeholder="Enter your last name"
              />
              {errors.lastName && (
                <p className="text-red-500 text-sm mt-1">{errors.lastName.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">
                Date of Birth <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                {...register('dateOfBirth')}
                className={`form-input ${errors.dateOfBirth ? 'border-red-500' : ''}`}
              />
              {errors.dateOfBirth && (
                <p className="text-red-500 text-sm mt-1">{errors.dateOfBirth.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                {...register('gender')}
                className={`form-input ${errors.gender ? 'border-red-500' : ''}`}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              {errors.gender && (
                <p className="text-red-500 text-sm mt-1">{errors.gender.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">
                Blood Group <span className="text-red-500">*</span>
              </label>
              <select
                {...register('bloodGroup')}
                className={`form-input ${errors.bloodGroup ? 'border-red-500' : ''}`}
              >
                <option value="">Select Blood Group</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
              </select>
              {errors.bloodGroup && (
                <p className="text-red-500 text-sm mt-1">{errors.bloodGroup.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">
                Father's Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('fatherName')}
                className={`form-input ${errors.fatherName ? 'border-red-500' : ''}`}
                placeholder="Enter father's name"
              />
              {errors.fatherName && (
                <p className="text-red-500 text-sm mt-1">{errors.fatherName.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">
                Mother's Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('motherName')}
                className={`form-input ${errors.motherName ? 'border-red-500' : ''}`}
                placeholder="Enter mother's name"
              />
              {errors.motherName && (
                <p className="text-red-500 text-sm mt-1">{errors.motherName.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">
                Nationality <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('nationality')}
                className={`form-input ${errors.nationality ? 'border-red-500' : ''}`}
                placeholder="e.g., Indian"
              />
              {errors.nationality && (
                <p className="text-red-500 text-sm mt-1">{errors.nationality.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">
                Religion <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('religion')}
                className={`form-input ${errors.religion ? 'border-red-500' : ''}`}
                placeholder="Enter religion"
              />
              {errors.religion && (
                <p className="text-red-500 text-sm mt-1">{errors.religion.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">
                Caste <span className="text-red-500">*</span>
              </label>
              <select
                {...register('caste')}
                className={`form-input ${errors.caste ? 'border-red-500' : ''}`}
              >
                <option value="">Select Caste</option>
                <option value="General">General</option>
                <option value="OBC">OBC</option>
                <option value="SC">SC</option>
                <option value="ST">ST</option>
              </select>
              {errors.caste && (
                <p className="text-red-500 text-sm mt-1">{errors.caste.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">
                Aadhaar Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('aadhaarNumber')}
                onChange={(e) => {
                  e.target.value = e.target.value.replace(/\D/g, '').slice(0, 12);
                  register('aadhaarNumber').onChange(e);
                }}
                className={`form-input ${errors.aadhaarNumber ? 'border-red-500' : ''}`}
                placeholder="12 digit Aadhaar"
                maxLength={12}
              />
              {errors.aadhaarNumber && (
                <p className="text-red-500 text-sm mt-1">{errors.aadhaarNumber.message}</p>
              )}
            </div>
          </div>

          {/* Address Information */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Address Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-3">
                <label className="form-label">
                  Street Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('address.street')}
                  className={`form-input ${errors.address?.street ? 'border-red-500' : ''}`}
                  placeholder="Enter complete street address"
                />
                {errors.address?.street && (
                  <p className="text-red-500 text-sm mt-1">{errors.address.street.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Village <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('address.village')}
                  className={`form-input ${errors.address?.village ? 'border-red-500' : ''}`}
                  placeholder="Enter village name"
                />
                {errors.address?.village && (
                  <p className="text-red-500 text-sm mt-1">{errors.address.village.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Post Office (PO) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('address.postOffice')}
                  className={`form-input ${errors.address?.postOffice ? 'border-red-500' : ''}`}
                  placeholder="Enter post office"
                />
                {errors.address?.postOffice && (
                  <p className="text-red-500 text-sm mt-1">{errors.address.postOffice.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Police Station (PS) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('address.policeStation')}
                  className={`form-input ${errors.address?.policeStation ? 'border-red-500' : ''}`}
                  placeholder="Enter police station"
                />
                {errors.address?.policeStation && (
                  <p className="text-red-500 text-sm mt-1">{errors.address.policeStation.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  District <span className="text-red-500">*</span>
                </label>
                <select
                  {...register('address.district')}
                  className={`form-input ${errors.address?.district ? 'border-red-500' : ''}`}
                >
                  <option value="">Select District</option>
                  {ODISHA_DISTRICTS.map((district) => (
                    <option key={district.name} value={district.name}>
                      {district.name}
                    </option>
                  ))}
                </select>
                {errors.address?.district && (
                  <p className="text-red-500 text-sm mt-1">{errors.address.district.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Block <span className="text-red-500">*</span>
                </label>
                <select
                  {...register('address.block')}
                  className={`form-input ${errors.address?.block ? 'border-red-500' : ''}`}
                  disabled={!selectedDistrict}
                >
                  <option value="">Select Block</option>
                  {availableBlocks.map((block) => (
                    <option key={block} value={block}>
                      {block}
                    </option>
                  ))}
                </select>
                {errors.address?.block && (
                  <p className="text-red-500 text-sm mt-1">{errors.address.block.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">State</label>
                <input
                  type="text"
                  {...register('address.state')}
                  className="form-input bg-gray-100"
                  value="Odisha"
                  readOnly
                />
              </div>

              <div>
                <label className="form-label">
                  Pincode <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('address.pincode')}
                  onChange={(e) => {
                    e.target.value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    register('address.pincode').onChange(e);
                  }}
                  className={`form-input ${errors.address?.pincode ? 'border-red-500' : ''}`}
                  placeholder="Enter 6-digit pincode"
                  maxLength={6}
                />
                {errors.address?.pincode && (
                  <p className="text-red-500 text-sm mt-1">{errors.address.pincode.message}</p>
                )}
              </div>
            </div>

            {/* Physical & Sports Details */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Physical & Sports Details</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="form-label">Height (cm) <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    step="0.1"
                    {...register('heightCm')}
                    className={`form-input ${errors.heightCm ? 'border-red-500' : ''}`}
                    placeholder="e.g., 170"
                  />
                  {errors.heightCm && (
                    <p className="text-red-500 text-sm mt-1">{errors.heightCm.message as string}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Weight (kg) <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    step="0.1"
                    {...register('weightKg')}
                    className={`form-input ${errors.weightKg ? 'border-red-500' : ''}`}
                    placeholder="e.g., 65"
                  />
                  {errors.weightKg && (
                    <p className="text-red-500 text-sm mt-1">{errors.weightKg.message as string}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Sports Category</label>
                  <input
                    type="text"
                    {...register('sportsCategory')}
                    className={`form-input ${errors.sportsCategory ? 'border-red-500' : ''}`}
                    placeholder="e.g., Athletics"
                  />
                  {errors.sportsCategory && (
                    <p className="text-red-500 text-sm mt-1">{errors.sportsCategory.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                <div>
                  <label className="form-label">Events</label>
                  <div className="flex items-center space-x-4 mt-2">
                    <label className="inline-flex items-center space-x-2">
                      <input type="checkbox" {...register('eventsStateLevel')} />
                      <span>State Level</span>
                    </label>
                    <label className="inline-flex items-center space-x-2">
                      <input type="checkbox" {...register('eventsNationalLevel')} />
                      <span>National Level</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="form-label">NCC Grade</label>
                  <select
                    {...register('nccGrade')}
                    className={`form-input ${errors.nccGrade ? 'border-red-500' : ''}`}
                  >
                    <option value="">Select</option>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                    <option value="None">None</option>
                  </select>
                  {errors.nccGrade && (
                    <p className="text-red-500 text-sm mt-1">{errors.nccGrade.message as string}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  {...register('mobileNumber')}
                  onChange={(e) => {
                    e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    register('mobileNumber').onChange(e);
                  }}
                  className={`form-input ${errors.mobileNumber ? 'border-red-500' : ''}`}
                  placeholder="Enter 10-digit mobile number"
                  maxLength={10}
                />
                {errors.mobileNumber && (
                  <p className="text-red-500 text-sm mt-1">{errors.mobileNumber.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Email ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  {...register('emailId')}
                  className={`form-input ${errors.emailId ? 'border-red-500' : ''}`}
                  placeholder="Enter your email address"
                />
                {errors.emailId && (
                  <p className="text-red-500 text-sm mt-1">{errors.emailId.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-gray-100">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="btn-secondary"
              >
                Back
              </button>
            )}
            
            <div className="flex flex-wrap items-center gap-3 ml-auto">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  handleSubmit((formData) => {
                    if (specialIdStatus.status === 'checking') {
                      alert('Please wait a moment while we verify the availability of the Form Number.');
                      return;
                    }
                    if (specialIdStatus.status === 'taken') {
                      alert(`The Form Number entered (${specialIdStatus.message || 'Already taken'}) is not available. Please choose a different number or leave it blank.`);
                      return;
                    }
                    onNext(formData, true);
                  })();
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2.5 rounded-lg text-sm shadow-sm transition-all flex items-center space-x-2 disabled:opacity-50"
                title="Submit application directly (skipping Steps 2 & 3)"
              >
                <CheckIcon className="w-5 h-5" />
                <span>{isSubmitting ? 'Submitting Application...' : 'Submit Application (Skip Steps 2 & 3)'}</span>
              </button>

              <button
                type="submit"
                className="btn-primary flex items-center space-x-2"
              >
                <span>Continue to Education Details</span>
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonalDetailsStep;